import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const USER_ID_KEY = "afritask.user_id";
const USERNAME_KEY = "afritask.username";
const TOKEN_KEY = "afritask.session_token";

export const getStoredUserId = () => localStorage.getItem(USER_ID_KEY) || null;
export const getStoredUsername = () => localStorage.getItem(USERNAME_KEY) || null;
export const getSessionToken = () => localStorage.getItem(TOKEN_KEY) || null;
export const setSessionToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearSession = () => {
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(USERNAME_KEY);
  localStorage.removeItem(TOKEN_KEY);
};

const persist = (u) => {
  if (u.user_id) localStorage.setItem(USER_ID_KEY, u.user_id);
  if (u.username) localStorage.setItem(USERNAME_KEY, u.username);
  if (u.session_token) localStorage.setItem(TOKEN_KEY, u.session_token);
};

const client = axios.create({
  baseURL: API,
  // Global 15s timeout — keeps any single request from hanging forever and
  // starving the browser's 6 concurrent-connection budget. Paystack verify
  // (the slowest call) typically returns in 3-5s.
  timeout: 15000,
});

client.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    // Auto-logout on session expiry (401 with expired session)
    if (err?.response?.status === 401 && /session expired/i.test(err?.response?.data?.detail || "")) {
      clearSession();
      if (!window.location.pathname.startsWith("/auth") && !window.location.pathname.startsWith("/legal")) {
        window.location.href = "/auth/enter-phone";
      }
    }
    // Auto-redirect on waitlisted 403 — every dashboard endpoint returns this
    // when the current user is on the waitlist. The backend nests our custom
    // body inside FastAPI's `detail`, so check both shapes defensively.
    if (err?.response?.status === 403) {
      const d = err?.response?.data?.detail;
      const code = (typeof d === "object" && d && d.code) || err?.response?.data?.code;
      if (code === "WAITLISTED" && !window.location.pathname.startsWith("/waitlist")) {
        window.location.href = "/waitlist";
      }
    }
    return Promise.reject(err);
  }
);

const PENDING_REF_KEY = "afritask.pending_referral_code";

// ---------- Phase 8.5 Auth ----------
export const authPhoneCheck = (phone, country) =>
  client.post("/auth/phone-check", { phone, country }).then((r) => r.data);
export const authSendCode = (phone, purpose = "signup", country) =>
  client.post("/auth/send-code", { phone, purpose, country }).then((r) => r.data);
export const authVerifyCode = (request_id, code, phone) =>
  client.post("/auth/verify-code", { request_id, code, phone }).then((r) => r.data);
export const authCreateAccount = (payload) =>
  client.post("/auth/create-account", payload).then((r) => r.data);
export const authSetPin = (payload) =>
  client.post("/auth/set-pin", payload).then((r) => r.data);
export const authLogin = (phone, pin) =>
  client.post("/auth/login", { phone, pin }).then((r) => r.data);
export const authResetPinRequest = (phone) =>
  client.post("/auth/reset-pin-request", { phone }).then((r) => r.data);
export const authResetPinComplete = (payload) =>
  client.post("/auth/reset-pin-complete", payload).then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);
export const authChangePin = (payload) =>
  client.post("/auth/change-pin", payload).then((r) => r.data);
export const authCheckReferral = (code) =>
  client.get(`/auth/check-referral/${encodeURIComponent(code)}`).then((r) => r.data);

// ---------- Phase 8.5 Profile ----------
export const getSkillsCatalog = () => client.get("/skills-catalog").then((r) => r.data);
export const getMyProfile = () => client.get("/me/profile").then((r) => r.data);
export const patchMyProfile = (patch) => client.patch("/me/profile", patch).then((r) => r.data);
export const completeMyProfile = () => client.post("/me/profile/complete").then((r) => r.data);

export const initUser = async (opts = {}) => {
  const body = { ...opts };
  if (!body.referral_code) {
    const stored = localStorage.getItem(PENDING_REF_KEY);
    if (stored) body.referral_code = stored;
  }
  const { data } = await client.post("/users/init", body);
  persist(data);
  if (data.referral_captured) {
    localStorage.removeItem(PENDING_REF_KEY);
  }
  return data;
};

/** Ensure a user exists — now requires a valid session token OR falls back to legacy anon. */
export const ensureUser = async () => {
  const token = getSessionToken();
  if (token) {
    try {
      const { data } = await client.get("/me");
      localStorage.setItem(USER_ID_KEY, data.user_id);
      localStorage.setItem(USERNAME_KEY, data.username);
      return data;
    } catch { /* fall through */ }
  }
  const uid = getStoredUserId();
  const uname = getStoredUsername();
  if (uid && uname) return { user_id: uid, username: uname };
  return null; // caller should redirect to /auth/enter-phone
};

export const getWallet = () => client.get("/wallet").then((r) => r.data);
export const listTransactions = () =>
  client.get("/wallet/transactions").then((r) => r.data.transactions);
export const listLedger = () =>
  client.get("/wallet/ledger").then((r) => r.data.entries);

export const topupInit = (amount_ngn) =>
  client.post("/wallet/topup-init", { amount_ngn }).then((r) => r.data);

export const verifyReference = (reference) =>
  client.get(`/wallet/verify/${reference}`).then((r) => r.data);

export const getLeaderboard = (limit = 20) =>
  client.get(`/leaderboard?limit=${limit}`).then((r) => r.data.entries);

export const getProfile = () => client.get("/me").then((r) => r.data);

// ---------- Phase 3 ----------
export const listTasks = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.append(k, v);
  });
  return client.get(`/tasks?${qs.toString()}`).then((r) => r.data.tasks);
};

export const listAvailableTasks = (limit = 5) =>
  client.get(`/tasks/available?limit=${limit}`).then((r) => r.data.tasks);

export const getTask = (id) => client.get(`/tasks/${id}`).then((r) => r.data);

export const enterTask = (id) =>
  client.post(`/tasks/${id}/enter`).then((r) => r.data);

export const getMyApplications = () =>
  client.get("/applications/mine").then((r) => r.data);

export const seedMyPipeline = () =>
  client.post("/dev/seed-my-pipeline").then((r) => r.data);

export const simulateSelectionDemo = () =>
  client.post("/dev/simulate-selection-demo").then((r) => r.data);

// ---------- Phase 4: Notifications ----------
export const listNotifications = (limit = 50) =>
  client.get(`/notifications?limit=${limit}`).then((r) => r.data);

export const markNotificationRead = (id) =>
  client.post(`/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  client.post("/notifications/read-all").then((r) => r.data);
export const deleteNotification = (nid) =>
  client.delete(`/notifications/${nid}`).then((r) => r.data);
export const deleteReadNotifications = () =>
  client.delete("/notifications?read=true").then((r) => r.data);

// ---------- Phase 5: Workspace ----------
export const getWorkspace = (id) =>
  client.get(`/tasks/${id}/workspace`).then((r) => r.data);

export const saveDraft = (id, draft_data) =>
  client.patch(`/tasks/${id}/workspace/draft`, { draft_data }).then((r) => r.data);

export const submitWorkspace = (id, submission_data) =>
  client.post(`/tasks/${id}/workspace/submit`, { submission_data }).then((r) => r.data);

// Phase C: partial answers autosave for the task-question stepper (dedicated endpoint).
export const saveDraftAnswers = (id, answers) =>
  client.post(`/tasks/${id}/workspace/draft`, { answers }).then((r) => r.data);

// Phase C2: worker ticks the authenticity checkbox on the intro screen.
export const acknowledgeAuthenticity = (id) =>
  client.post(`/tasks/${id}/workspace/acknowledge`).then((r) => r.data);

export const getEarningsHistory = () =>
  client.get("/applications/earnings-history").then((r) => r.data.entries);

export const simulateFullFlow = (task_type) =>
  client.post(`/dev/simulate-full-flow/${task_type}`).then((r) => r.data);

// ---------- Phase 5: Admin ----------
const ADMIN_TOKEN_KEY = "afritask.admin_token";
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

const adminClient = axios.create({ baseURL: API });
adminClient.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) config.headers["X-Admin-Token"] = t;
  return config;
});

export const adminGetDashboard = () =>
  adminClient.get("/admin/dashboard-stats").then((r) => r.data);

// AfriSelection AI admin
export const afriListTasks = (status = "awaiting_ranking,ranked") =>
  adminClient.get(`/admin/afriselection/tasks?status=${encodeURIComponent(status)}`).then((r) => r.data);
export const afriRankTask = (taskId) =>
  adminClient.post(`/admin/afriselection/tasks/${taskId}/rank`).then((r) => r.data);
export const afriRankingStatus = (taskId) =>
  adminClient.get(`/admin/afriselection/tasks/${taskId}/ranking-status`).then((r) => r.data);
export const afriTaskResults = (taskId) =>
  adminClient.get(`/admin/afriselection/tasks/${taskId}/results`).then((r) => r.data);
export const afriAcceptTask = (taskId) =>
  adminClient.post(`/admin/afriselection/tasks/${taskId}/accept`).then((r) => r.data);
export const afriLockTask = (taskId) =>
  adminClient.post(`/admin/afriselection/tasks/${taskId}/lock`).then((r) => r.data);
export const afriListSubmissions = (taskId) =>
  adminClient.get(`/admin/afriselection/tasks/${taskId}/submissions`).then((r) => r.data);
export const afriRejectSubmission = (taskId, entryId) =>
  adminClient
    .post(`/admin/afriselection/tasks/${taskId}/submissions/${entryId}/reject`)
    .then((r) => r.data);
export const afriUpdateTaskConfig = (taskId, patch) =>
  adminClient.patch(`/admin/afriselection/tasks/${taskId}/config`, patch).then((r) => r.data);
export const adminPreviewGrade = (body) =>
  adminClient.post(`/admin/afriselection/preview-grade`, body).then((r) => r.data);

// Platform-wide activity feed (Recent Activity widget on Dashboard)
export const getActivityFeed = (limit = 15) =>
  client.get(`/activity/feed?limit=${limit}`).then((r) => r.data);

export const adminGetMetrics = () =>
  adminClient.get("/admin/metrics/overview").then((r) => r.data);

export const adminListSelectionLogs = (limit = 100) =>
  adminClient.get(`/admin/selection-logs?limit=${limit}`).then((r) => r.data);

export const adminSelectionLogDetail = (taskId) =>
  adminClient.get(`/admin/selection-logs/${taskId}`).then((r) => r.data);

export const adminGetSettings = () =>
  adminClient.get("/admin/settings").then((r) => r.data);

export const adminUpdateSettings = (config) =>
  adminClient.put("/admin/settings", { config }).then((r) => r.data);

export const adminSetFeatureFlag = (key, value) =>
  adminClient.patch("/admin/settings/feature-flag", { key, value }).then((r) => r.data);

export const completeOnboarding = () =>
  client.post("/me/complete-onboarding").then((r) => r.data);
export const adminListReviews = (task_type) => {
  const qs = task_type && task_type !== "all" ? `?task_type=${task_type}` : "";
  return adminClient.get(`/admin/reviews${qs}`).then((r) => r.data);
};
export const adminGetReview = (id) =>
  adminClient.get(`/admin/reviews/${id}`).then((r) => r.data);
export const adminApprove = (id, payout_points) =>
  adminClient.post(`/admin/reviews/${id}/approve`, { payout_points }).then((r) => r.data);
export const adminReject = (id, reason) =>
  adminClient.post(`/admin/reviews/${id}/reject`, { reason }).then((r) => r.data);
export const adminRequestRevision = (id, note) =>
  adminClient.post(`/admin/reviews/${id}/request-revision`, { note }).then((r) => r.data);
export const adminListTasks = () =>
  adminClient.get("/admin/tasks").then((r) => r.data.tasks);
export const adminCreateTask = (body) =>
  adminClient.post("/admin/tasks", body).then((r) => r.data);
export const adminGetInstructionTemplate = (task_type) =>
  adminClient.get(`/admin/instruction-template?task_type=${encodeURIComponent(task_type)}`).then((r) => r.data);
export const adminDeleteTask = (id, confirm = false) =>
  adminClient.delete(`/admin/tasks/${id}${confirm ? "?confirm=true" : ""}`).then((r) => r.data);
export const adminListUsers = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.country) qs.set("country", params.country);
  if (params.countries) qs.set("countries", params.countries);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString();
  return adminClient
    .get(`/admin/users${suffix ? `?${suffix}` : ""}`)
    .then((r) => r.data);
};
export const getPublicCountries = () =>
  client.get(`/public/countries`).then((r) => r.data);
export const adminAdjustBalance = (id, body) =>
  adminClient.post(`/admin/users/${id}/adjust`, body).then((r) => r.data);
export const adminGetAdjustments = (id) =>
  adminClient.get(`/admin/users/${id}/adjustments`).then((r) => r.data.adjustments);
export const adminBanUser = (id, reason = "") =>
  adminClient.post(`/admin/users/${id}/ban`, { reason }).then((r) => r.data);
export const adminUnbanUser = (id) =>
  adminClient.post(`/admin/users/${id}/unban`).then((r) => r.data);
export const adminDeleteUser = (id, confirm = false) =>
  adminClient.delete(`/admin/users/${id}${confirm ? "?confirm=true" : ""}`).then((r) => r.data);
export const adminWipeTestData = (confirm = false) =>
  adminClient.post(`/admin/wipe-test-data${confirm ? "?confirm=true" : ""}`).then((r) => r.data);
export const adminNuclearWipeDryRun = () =>
  adminClient.post("/admin/nuclear-wipe").then((r) => r.data);
export const adminNuclearWipeExecute = () =>
  adminClient
    .post("/admin/nuclear-wipe?confirm=true&i_understand=YES_NUKE_EVERYTHING")
    .then((r) => r.data);
export const adminForceSelect = (task_id) =>
  adminClient.post(`/admin/tasks/${task_id}/force-select`).then((r) => r.data);
export const adminSelectionLog = (task_id) =>
  adminClient.get(`/admin/tasks/${task_id}/selection-log`).then((r) => r.data);

// ---------- Phase 6: Bank accounts + withdrawals ----------
export const listBanks = () =>
  client.get("/banks").then((r) => r.data.banks);

export const resolveBankAccount = (account_number, bank_code) =>
  client.post("/bank-accounts/resolve", { account_number, bank_code }).then((r) => r.data);

export const listBankAccounts = () =>
  client.get("/bank-accounts").then((r) => r.data.bank_accounts);

export const saveBankAccount = (body) =>
  client.post("/bank-accounts", body).then((r) => r.data);

export const deleteBankAccount = (id) =>
  client.delete(`/bank-accounts/${id}`).then((r) => r.data);

export const setDefaultBank = (id) =>
  client.post(`/bank-accounts/${id}/set-default`).then((r) => r.data);

export const requestWithdrawal = (amount_ngn, bank_account_id) =>
  client.post("/withdrawals", { amount_ngn, bank_account_id }).then((r) => r.data);

export const listMyWithdrawals = () =>
  client.get("/withdrawals/mine").then((r) => r.data.withdrawals);

export const cancelWithdrawal = (id) =>
  client.post(`/withdrawals/${id}/cancel`).then((r) => r.data);

export const simulateSuccessfulWithdrawal = (id) =>
  client.post(`/dev/simulate-successful-withdrawal/${id}`).then((r) => r.data);

export const simulateFailedWithdrawal = (id) =>
  client.post(`/dev/simulate-failed-withdrawal/${id}`).then((r) => r.data);

// Admin — withdrawals
export const adminListWithdrawals = (status = "all") =>
  adminClient.get(`/admin/withdrawals?status=${status}`).then((r) => r.data);

export const adminGetWithdrawal = (id) =>
  adminClient.get(`/admin/withdrawals/${id}`).then((r) => r.data);

export const adminApproveWithdrawal = (id) =>
  adminClient.post(`/admin/withdrawals/${id}/approve`).then((r) => r.data);

export const adminMarkWithdrawalPaid = (id, manual_transfer_reference) =>
  adminClient
    .post(`/admin/withdrawals/${id}/mark-paid`, { manual_transfer_reference })
    .then((r) => r.data);

export const adminRejectWithdrawal = (id, reason) =>
  adminClient.post(`/admin/withdrawals/${id}/reject`, { reason }).then((r) => r.data);

export const adminPaystackTransfersStatus = () =>
  adminClient.get(`/admin/paystack/transfers-status`).then((r) => r.data);

// ---------- Phase 7: Leaderboard / Stats / Badges ----------
export const getLeaderboardV2 = (period = "week", category = "earnings", limit = 20) =>
  client.get(`/leaderboard?period=${period}&category=${category}&limit=${limit}`).then((r) => r.data);

export const getMyRanks = () =>
  client.get("/leaderboard/me").then((r) => r.data);

export const getMyStats = () =>
  client.get("/me/stats").then((r) => r.data);

export const getMyBadges = () =>
  client.get("/me/badges").then((r) => r.data);

export const markBadgesSeen = () =>
  client.post("/me/badges/mark-seen").then((r) => r.data);

export const listAllBadges = () =>
  client.get("/badges").then((r) => r.data.badges);

export const devSimulateStreak = (userId, days = 3) =>
  client.post(`/dev/simulate-streak/${userId}?days=${days}`).then((r) => r.data);

export const devSimulateApprovals = (userId, count = 5) =>
  client.post(`/dev/simulate-approvals/${userId}?count=${count}`).then((r) => r.data);

export const devResetMyBadges = () =>
  client.post("/dev/reset-my-badges").then((r) => r.data);

// ---------- Phase 8: Referrals ----------
export const getMyReferral = () =>
  client.get("/me/referral").then((r) => r.data);

export const listMyReferrals = () =>
  client.get("/me/referrals").then((r) => r.data.referrals);

export const validateReferralCode = (code) =>
  client.post("/referrals/validate-code", { code }).then((r) => r.data);

// Public platform stats (landing page — no auth)
export const getPlatformStats = () =>
  client.get("/public/platform-stats").then((r) => r.data);

export const initUserWithReferral = (payload) =>
  client.post("/users/init", payload).then((r) => r.data);

export const adminListReferrals = (status = "all") =>
  adminClient.get(`/admin/referrals?status=${status}`).then((r) => r.data);

export const adminReferralDetail = (id) =>
  adminClient.get(`/admin/referrals/${id}`).then((r) => r.data);

export const adminFlagReferral = (id, reason) =>
  adminClient.post(`/admin/referrals/${id}/flag`, { reason }).then((r) => r.data);

export const adminUnflagReferral = (id) =>
  adminClient.post(`/admin/referrals/${id}/unflag`).then((r) => r.data);

export const devSimulateReferralSignup = (referrerUid) =>
  client.post(`/dev/simulate-referral-signup/${referrerUid}`).then((r) => r.data);

export const devSimulateReferralActivation = (referralId, trigger = "first_topup") =>
  client.post(`/dev/simulate-referral-activation/${referralId}?trigger=${trigger}`).then((r) => r.data);

// ─── Support chat + ticket ───────────────────────────────
export const sendChatMessage = (message, session_id = null) =>
  client.post("/chat", { message, session_id }).then((r) => r.data);
export const submitSupportTicket = (payload) =>
  client.post("/support/ticket", payload).then((r) => r.data);
export const adminListSupportTickets = (status = null) => {
  const q = status ? `?status=${status}` : "";
  return adminClient.get(`/admin/support-tickets${q}`).then((r) => r.data);
};
export const adminResolveTicket = (id) =>
  adminClient.post(`/admin/support-tickets/${id}/resolve`).then((r) => r.data);

// ---------- Waitlist ----------
export const getWaitlistStatus = () =>
  client.get(`/waitlist/status`).then((r) => r.data);
export const getPublicPlatformStatus = () =>
  client.get(`/public/platform-status`).then((r) => r.data);

// ---- Admin waitlist
export const adminListWaitlist = ({ page = 1, size = 50, search } = {}) => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  if (search) params.set("search", search);
  return adminClient.get(`/admin/waitlist?${params.toString()}`).then((r) => r.data);
};
export const adminAdmitAllWaitlist = () =>
  adminClient.post(`/admin/waitlist/admit-all`, { confirm: "ADMIT_ALL" }).then((r) => r.data);
export const adminAdmitWaitlistUser = (userId) =>
  adminClient.post(`/admin/waitlist/admit/${userId}`).then((r) => r.data);
